// Types
import { Role } from '../../../../types/auth';
import { OrderState } from '../../../../types/orders';

// Utils
import { getSessionStorageUser } from '../../../../utils/sessionStorage';

/** Dónde se edita «Datos Act»: el asistente (alta/completado) o la validación. */
export type BasicScheduleFlow = 'wizard' | 'validation';

/**
 * Horario de la Actuació bàsica en «Datos Act» (plan 2026-10-08). En la
 * VALIDACIÓN lo corrige gestión; la tablet y Sala solo lo ven. En el
 * ASISTENTE (alta/completado) lo pone quien hace la básica, también la tablet
 * (decisión del usuario, 12-oct); Sala no, que es de solo lectura.
 */
const VALIDATION_SCHEDULE_EDITORS: readonly string[] = [
  Role.SuperAdmin,
  Role.Coex,
  Role.Capataz,
];

export const roleCanEditBasicSchedule = (
  role: string | null | undefined,
  flow: BasicScheduleFlow
): boolean => {
  if (!role) return false;
  if (flow === 'wizard') return role !== Role.RoomTeam;
  return VALIDATION_SCHEDULE_EDITORS.includes(role);
};

/**
 * ¿Se puede tocar el horario? Además del rol, igual que las fechas de
 * Operarios en la actuación normal: deshabilitado con la actuación anulada o
 * con el padre (el recorrido) anulado o finalizado. Lo usan el formulario
 * (campos y regla fin ≥ inicio) y la validación de fuera, para que digan lo
 * mismo.
 */
export const isBasicScheduleEditable = (
  canEdit: boolean,
  orderState: string | null | undefined,
  parentState?: string | null
): boolean =>
  canEdit &&
  orderState !== OrderState.Annulled &&
  parentState !== OrderState.Annulled &&
  parentState !== OrderState.End;

/**
 * Contexto de yup de `EditingActionSchema` (revisión 12-oct, R2): la regla
 * fin ≥ inicio solo se aplica con el horario EDITABLE. Con los campos
 * deshabilitados (tablet, Sala, anulada), un fin < inicio que dejó el back no
 * puede bloquear el avance: nadie lo podría corregir.
 */
export type EditingActionSchemaContext = {
  basicScheduleEditable?: boolean;
};

/**
 * El contexto del esquema para quien valida «Datos Act» FUERA del formulario
 * (la validación central y el «Validar» del resumen). Una sola función a
 * propósito: el resumen validaba sin contexto y dejaba validar con fin <
 * inicio sin avisar (bug del 12-oct, At.0000523.0000.02). Mismas piezas que
 * el formulario (`roleCanEditBasicSchedule` + `isBasicScheduleEditable`), así
 * que «editable» sale igual en los tres sitios.
 */
export const basicScheduleSchemaContext = ({
  flow,
  role,
  orderState,
  parentState,
}: {
  flow: BasicScheduleFlow;
  role: string | null | undefined;
  orderState: string | null | undefined;
  parentState?: string | null;
}): EditingActionSchemaContext => ({
  basicScheduleEditable: isBasicScheduleEditable(
    roleCanEditBasicSchedule(role, flow),
    orderState,
    parentState
  ),
});

/** Lo mismo, con el rol de la sesión y los estados del formulario guardado. */
export const basicScheduleContextFromSession = (
  flow: BasicScheduleFlow,
  editionData:
    | { state?: unknown; parentExpeditionOrderStatus?: unknown }
    | null
    | undefined
): EditingActionSchemaContext =>
  basicScheduleSchemaContext({
    flow,
    role: getSessionStorageUser()?.user?.rolActive,
    orderState: editionData?.state as string | undefined,
    parentState: editionData?.parentExpeditionOrderStatus as string | undefined,
  });

/**
 * Las tres horas del bloque (12-oct): inicio y fin viven en la Order, la
 * llegada en la Action. Inicio y fin los calcula el back con el hueco del
 * tramo; la llegada no la calcula nadie (vacía hasta que alguien la pone).
 */
export const BASIC_SCHEDULE_KEYS = [
  'registerInitDate',
  'registerArrivalDate',
  'registerEndDate',
] as const;
export type BasicScheduleKey = (typeof BASIC_SCHEDULE_KEYS)[number];
/** ISO, o '' = vacío. */
export type BasicSchedule = Record<BasicScheduleKey, string>;

/** ¿La misma hora? Vacío ('' / null) solo es igual a vacío; si no, el instante. */
export const sameScheduleValue = (a: unknown, b: unknown): boolean => {
  const isEmpty = (v: unknown) => v == null || v === '';
  if (isEmpty(a) || isEmpty(b)) return isEmpty(a) && isEmpty(b);
  const timeA = new Date(a as string).getTime();
  const timeB = new Date(b as string).getTime();
  return Number.isNaN(timeA) || Number.isNaN(timeB) ? a === b : timeA === timeB;
};

/**
 * Horario que enseña «Datos Act» (revisión 12-oct, R1). La sesión de la
 * pestaña puede ser de una visita ANTERIOR: se pasó por aquí con el recorrido
 * en marcha (fin vacío), se salió sin guardar y el back estampó el fin. Si la
 * sesión ganaba, el formulario enseñaba el fin vacío y el envío lo tomaba por
 * «papelera»: el back BORRABA el fin estampado.
 *
 * Cada hora de la sesión manda solo si difiere de lo que ESA MISMA sesión
 * guardó del servidor (`session[baselineKey]`), es decir, si el usuario la
 * tocó. Si no hay con qué comparar, manda el servidor: lo no tocado nunca viaja.
 */
export const resolveBasicSchedule = (
  fresh: BasicSchedule,
  session: Record<string, unknown> | undefined,
  baselineKey: string
): BasicSchedule => {
  const savedFromServer = session?.[baselineKey] as
    | Record<string, unknown>
    | undefined;
  const pick = (key: BasicScheduleKey): string => {
    if (!session || !savedFromServer || !(key in session)) return fresh[key];
    return sameScheduleValue(session[key], savedFromServer[key])
      ? fresh[key]
      : ((session[key] as string | null) ?? '');
  };
  return {
    registerInitDate: pick('registerInitDate'),
    registerArrivalDate: pick('registerArrivalDate'),
    registerEndDate: pick('registerEndDate'),
  };
};

/**
 * Clave del formulario del ASISTENTE donde se guarda el horario tal como se
 * cargó (la validación usa `dataFromServer`). Contra ella se decide qué cambió
 * el usuario: lo no tocado nunca viaja (punto 4, 12-oct).
 */
export const BASIC_SCHEDULE_BASELINE_KEY = 'basicScheduleFromServer';

/** Lo que el asistente manda del horario: ausente = no tocar, null = borrar. */
export type BasicScheduleInput = {
  registerInitDate?: string;
  registerArrivalDate?: string | null;
  registerEndDate?: string | null;
};

/**
 * Qué horas cambió el usuario en «Datos Act» del asistente respecto a lo que
 * se cargó (`values[BASIC_SCHEDULE_BASELINE_KEY]`). Igual que el diff de la
 * validación: solo viaja lo cambiado; vaciar el fin o la llegada con la
 * papelera viaja como `null` (borrar). El inicio no se puede vaciar: un inicio
 * vacío no viaja (el back lo ignora igual). Sin base guardada no se sabe qué
 * se tocó, así que no viaja nada.
 *
 * Como los valores y la base viajan juntos en el formulario, el reintento de
 * la fase 2 encolada sin red manda lo mismo: un campo que el usuario no tocó
 * no puede pisar lo que el servidor haya estampado mientras tanto.
 */
export const basicScheduleChanges = (
  values: Record<string, unknown> | null | undefined
): BasicScheduleInput => {
  const baseline = values?.[BASIC_SCHEDULE_BASELINE_KEY] as
    | Record<string, unknown>
    | undefined;
  if (!values || !baseline) return {};
  const changed = (key: BasicScheduleKey) =>
    key in values && !sameScheduleValue(values[key], baseline[key]);
  const valueOf = (key: BasicScheduleKey) => {
    const value = values[key];
    return value == null || value === '' ? null : String(value);
  };
  const input: BasicScheduleInput = {};
  if (changed('registerInitDate') && valueOf('registerInitDate')) {
    input.registerInitDate = valueOf('registerInitDate') as string;
  }
  if (changed('registerArrivalDate')) {
    input.registerArrivalDate = valueOf('registerArrivalDate');
  }
  if (changed('registerEndDate')) {
    input.registerEndDate = valueOf('registerEndDate');
  }
  return input;
};
